import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface ClassTargetingProps {
  availableClasses: string[];
  selectedClasses: string[];
  onChange: (classes: string[]) => void;
}

export function ClassTargeting({ availableClasses, selectedClasses, onChange }: ClassTargetingProps) {
  const allSelected = availableClasses.length > 0 && availableClasses.every(c => selectedClasses.includes(c));

  const toggleAll = () => {
    onChange(allSelected ? [] : [...availableClasses]);
  };

  const toggleClass = (cls: string) => {
    onChange(
      selectedClasses.includes(cls)
        ? selectedClasses.filter(c => c !== cls)
        : [...selectedClasses, cls]
    );
  };

  return (
    <div className="space-y-2">
      <Label>Targeted Student Users by Class</Label>
      <p className="text-xs text-muted-foreground">Select which student classes can see this content. All staff users always see it.</p>
      <div className="flex flex-wrap gap-x-4 gap-y-2 border rounded-md p-3">
        <label className="flex items-center gap-1.5 cursor-pointer font-medium">
          <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
          <span className="text-sm">All classes</span>
        </label>
        <div className="w-full border-t my-1" />
        {availableClasses.map(cls => (
          <label key={cls} className="flex items-center gap-1.5 cursor-pointer">
            <Checkbox
              checked={selectedClasses.includes(cls)}
              onCheckedChange={() => toggleClass(cls)}
            />
            <span className="text-sm">{cls}</span>
          </label>
        ))}
        {availableClasses.length === 0 && (
          <p className="text-xs text-muted-foreground">No classes found. Add courses first.</p>
        )}
      </div>
    </div>
  );
}

interface ExpiryControlsProps {
  expiryMode: string;
  expiresAt: Date | null;
  onModeChange: (mode: string) => void;
  onDateChange: (date: Date | null) => void;
}

export function ExpiryControls({ expiryMode, expiresAt, onModeChange, onDateChange }: ExpiryControlsProps) {
  const [durationValue, setDurationValue] = useState<number>(7);
  const [durationUnit, setDurationUnit] = useState<"days" | "weeks" | "months">("days");

  const applyDuration = () => {
    const d = new Date();
    if (durationUnit === "days") d.setDate(d.getDate() + durationValue);
    else if (durationUnit === "weeks") d.setDate(d.getDate() + durationValue * 7);
    else d.setMonth(d.getMonth() + durationValue);
    onDateChange(d);
  };

  return (
    <div className="space-y-2">
      <Label>Visibility Expiry</Label>
      <Select value={expiryMode} onValueChange={v => {
        onModeChange(v);
        if (v === "none") onDateChange(null);
      }}>
        <SelectTrigger><SelectValue placeholder="Select expiry type" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">No expiry</SelectItem>
          <SelectItem value="date">Pick a date</SelectItem>
          <SelectItem value="duration">Set a duration</SelectItem>
        </SelectContent>
      </Select>

      {expiryMode === "date" && (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !expiresAt && "text-muted-foreground")}>
              <CalendarIcon className="mr-2 h-4 w-4" />
              {expiresAt ? format(expiresAt, "PPP") : "Pick expiry date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={expiresAt || undefined}
              onSelect={d => onDateChange(d || null)}
              disabled={d => d < new Date()}
              initialFocus
              className="p-3 pointer-events-auto"
            />
          </PopoverContent>
        </Popover>
      )}

      {expiryMode === "duration" && (
        <div className="space-y-2">
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Label className="text-xs">Value</Label>
              <Input
                type="number"
                min={1}
                max={365}
                value={durationValue}
                onChange={e => setDurationValue(Math.max(1, parseInt(e.target.value) || 1))}
              />
            </div>
            <div className="flex-1">
              <Label className="text-xs">Unit</Label>
              <Select value={durationUnit} onValueChange={v => setDurationUnit(v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="days">Days</SelectItem>
                  <SelectItem value="weeks">Weeks</SelectItem>
                  <SelectItem value="months">Months</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="button" size="sm" onClick={applyDuration}>Apply</Button>
          </div>
          {expiresAt && (
            <p className="text-xs text-muted-foreground">Expires: {format(expiresAt, "PPP")}</p>
          )}
        </div>
      )}
    </div>
  );
}
